import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import moment from 'moment';
import {
    FaHourglassStart, 
    FaUserGraduate
} from 'react-icons/fa';
import { BiRadar } from 'react-icons/bi';
import CoursePlaceholderImage from 'assets/vectors/CoursePlaceholder.svg';
import TestPlaceholderImage from 'assets/vectors/Practice.svg';
import axios from 'config/axios';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import Dropdown from 'components/Dropdown';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { updateCart } from 'store/cart';
import { setError, setAlert } from 'store/error';
import { Course, Bundle, Test } from 'global/types';
import './index.css';

function Courses() {
    const [courses, setCourses] = useState<Course[]>([]);
    const [bundles, setBundles] = useState<Bundle[]>([]);
    const [tests, setTests] = useState<Test[]>([]);
    const [numTests, setNumTests] = useState(0);
    const [testPrice, setTestPrice] = useState(0);
    const [testDiscount, setTestDiscount] = useState(null);

    const { user } = useAppSelector(state => state.user);
    const {
        bundles: cartBundles,
        courses: cartCourses,
        tests: cartTests,
        isUpdatingCart
    } = useAppSelector(state => state.cart);
    const { courses: classroomCourses } = useAppSelector(state => state.classroom);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const refreshCart = () => {
        axios.get('payments/cart/')
            .then(res => {
                // console.log(res.data);
                dispatch(updateCart({
                    bundles: res.data?.cartBundles,
                    courses: res.data?.cartCourses,
                    tests: res.data?.cartTests
                }));
            });
    }

    const addBundleToCart = (bundleId: string) => {
        if (user) {
            axios.post(`payments/cart/add/bundle/${bundleId}/`)
                .then(res => {
                    // console.log(res.data);
                    refreshCart();

                    ReactGA.event('add_to_cart', {
                        currency: 'USD',
                        value: res.data.price,
                        items: [{
                            item_id: bundleId,
                            item_name: res.data.name
                        }]
                    });
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    const addCourseToCart = (courseUrl: string) => {
        if (user) {
            axios.post(`payments/cart/add/course/${courseUrl}/`)
                .then(res => {
                    // console.log(res.data);
                    refreshCart();

                    ReactGA.event('add_to_cart', {
                        currency: 'USD',
                        value: res.data.tuition,
                        items: [{
                            item_id: courseUrl,
                            item_name: res.data.name
                        }]
                    });
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    const getTestPrice = (numTests: number) => {
        axios.get(`payments/cart/price/digital/${numTests}`)
            .then(res => {
                // console.log(res.data);
                setNumTests(numTests);
                setTestPrice(res.data.price);
                setTestDiscount(res.data.discount);
            });
    }

    const updateTestsInCart = () => {
        if (user) {
            if (!numTests) {
                dispatch(setError({ message: 'Select number of tests.' }));
                return;
            }
            axios.post(`payments/cart/update/tests/${numTests}/`)
                .then(res => {
                    const { addedTests, removedTests } = res.data;

                    refreshCart();

                    for (let i = 0; i < addedTests.length; i++) {
                        ReactGA.event('add_to_cart', {
                            currency: 'USD',
                            value: addedTests[i].price,
                            items: [{
                                item_id: addedTests[i].testId,
                                item_name: addedTests[i].name
                            }]
                        });
                    }
                    for (let i = 0; i < removedTests.length; i++) {
                        ReactGA.event('remove_from_cart', {
                            currency: 'USD',
                            value: removedTests[i].price,
                            items: [{
                                item_id: removedTests[i].testId,
                                item_name: removedTests[i].name
                            }]
                        });
                    }
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    const powerSet = (array: any[]) => {
        return new Array(1 << array.length).fill(null).map((_e1, i) => (
            array.filter((_e2, j) => i & 1 << j)
        )).sort((a, b) => b.length - a.length);
    }

    useEffect(() => {
        axios.get('courses/')
            .then(res => {
                // console.log(res.data);
                const {
                    bundles,
                    courses,
                    tests
                } = res.data;

                setBundles(bundles);
                setCourses(courses);
                setTests(tests);

                // ReactGA.event('view_item', {
                //     currency: 'USD',
                //     items: [
                //         ...bundles && bundles.filter(bundle => (
                //             !bundle.numRequired
                //         )).reduce((bundleItemList, bundle) => (
                //             [
                //                 ...bundleItemList,
                //                 {
                //                     item_id: bundle.id,
                //                     item_name: bundle.name,
                //                     price: bundle.courses?.reduce((total, course) => (
                //                             total + course.tuition
                //                         ), 0) + bundle.tests?.reduce((total, test) => (
                //                             total + test.price
                //                         ), 0),
                //                     discount: bundle.discount?.type === 'PERCENT'
                //                         ?   (bundle.courses?.reduce((total, course) => (
                //                                 total + course.tuition
                //                             ), 0) + bundle.tests?.reduce((total, test) => (
                //                                 total + test.price
                //                             ), 0)) * bundle.discount.value / 100
                //                         :   bundle.discount?.type === 'AMOUNT'
                //                         ?   bundle.discount?.value
                //                         :   0
                //                 }
                //             ]
                //         ), []),
                //         ...courses && courses.map(course => ({
                //             item_id: course.urlSlug,
                //             item_name: course.name,
                //             price: course.tuition,
                //             discount: course.discount?.type === 'PERCENT'
                //                 ?   course.tuition * course.discount.value / 100
                //                 :   course.discount?.type === 'AMOUNT'
                //                 ?   course.discount.value
                //                 :   0
                //         })),
                //         ...tests && tests.map(test => ({
                //             item_id: test.testId,
                //             item_name: test.name,
                //             price: test.price,
                //             discount: test.discount?.type === 'PERCENT'
                //                 ?   test.price * test.discount.value / 100
                //                 :   test.discount?.type === 'AMOUNT'
                //                 ?   test.discount.value
                //                 :   0
                //         }))
                //     ]
                // });
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });

        if (user) refreshCart();
    }, []);

    useEffect(() => {
        setNumTests(cartTests.length);
        getTestPrice(cartTests.length);
    }, [cartTests]);

    if (user && isUpdatingCart) return <></>;

    return (
        <div className='layout'>
            <Helmet
                title='Available Courses - The SAT Crash Course'
                description='Purchase individual courses or buy in bundles based on the results from the free detailed report card. Get discounts when purchasing in bundles.'
            />
            <h1 className='register__header'>
                <BiRadar className='register__headerIcon' />
                The SAT Crash Course
            </h1>
            <div className='register__content'>
                <div className='register__courses'>
                    {courses.map(course => (
                        <div className='register__course' key={course.name}>
                            <img
                                className='register__courseImage'
                                src={course.image
                                    ? course.image
                                    : CoursePlaceholderImage
                                }
                                alt={course.name}
                            />
                            <div className='register__courseContent'>
                                <div className='register__courseColumn'>
                                    <h3 className='register__courseTitle'>
                                        {course.name}
                                    </h3>
                                    <div className='register__courseInfo'>
                                        {course.teacher &&
                                            <p className='register__courseInfoItem'>
                                                <FaUserGraduate className='register__courseInfoIcon' />
                                                Teacher {course.teacher.firstName} {course.teacher.lastName}
                                            </p>
                                        }
                                        {course.period &&
                                            <p className='register__courseInfoItem'>
                                                <FaHourglassStart className='register__courseInfoIcon' />
                                                {moment.duration(course.period).asWeeks()}-Week Course
                                            </p>
                                        }
                                    </div>
                                    {course.discount
                                        ?   <p className='register__coursePrice--discount'>
                                                <span>${course.tuition.toFixed(2)}</span>
                                                ${course.discount?.type === 'Percent'
                                                    ? (course.tuition - (course.discount.value * course.tuition)/100).toFixed(2)
                                                    : course.discount?.type === 'Amount'
                                                    ? (course.tuition - course.discount.value).toFixed(2)
                                                    : course.tuition.toFixed(2)
                                                }
                                            </p>
                                        :   <p className='register__coursePrice'>
                                                ${course.tuition.toFixed(2)}
                                            </p>
                                    }
                                </div>
                                <div className='register__courseColumn'>
                                    <ButtonLink
                                        className='register__courseButton'
                                        href={`https://thesatcrashcourse.com/${course.urlSlug}`}
                                        secondary
                                    >
                                        LEARN MORE
                                    </ButtonLink>
                                    {classroomCourses.some(classroomCourse => classroomCourse.urlSlug === course.urlSlug)
                                        ?   <Button
                                                className='register__courseButton'
                                                disabled
                                            >
                                                REGISTERED
                                            </Button>
                                        :   cartCourses.some(cartCourse => cartCourse.urlSlug === course.urlSlug)
                                            || cartBundles.some(cartBundle => cartBundle.courses.some(bundleCourse => bundleCourse.urlSlug === course.urlSlug))
                                        ?   <Button 
                                                className='register__courseButton'
                                                disabled
                                            >
                                                CART ADDED
                                            </Button>
                                        :   <Button 
                                                className='register__courseButton'
                                                onClick={() => addCourseToCart(course.urlSlug)}
                                            >
                                                ADD TO CART
                                            </Button>
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                    {tests.length > 0 && <>
                        <h2 className='register__header'>
                            <BiRadar className='register__headerIcon' />
                            Practice and Prepare
                        </h2>
                        <div className='register__course'>
                            <img
                                className='register__courseImage'
                                src={TestPlaceholderImage}
                            />
                            <div className='register__courseContent'>
                                <div className='register__courseColumn'>
                                    <h3 className='register__courseTitle'>
                                        Digital SAT Practice Test
                                    </h3>
                                    <table className='register__numTestsDiscount'>
                                        <tbody>
                                            {/* <tr>
                                                <td colSpan={2}><b>$24.99</b> &nbsp;per test</td>
                                            </tr> */}
                                            <tr>
                                                <th>1+ Test</th>
                                                <td><b><span>$40.00</span><sub>/test</sub></b>$19.99<sub>/test</sub></td>
                                            </tr>
                                            <tr>
                                                <th>10+ Tests</th>
                                                <td><b><span>$40.00</span><sub>/test</sub></b>$17.50<sub>/test</sub></td>
                                                {/* <td><span>37.5% Of</span></td> */}
                                            </tr>
                                            <tr>
                                                <th>20 Tests</th>
                                                <td><b><span>$40.00</span><sub>/test</sub></b>$15.00<sub>/test</sub></td>
                                                {/* <td><span>50% Off</span></td> */}
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p className='register__numTestsLabel'>
                                        Enter number of tests to purchase
                                    </p>
                                    <Dropdown
                                        className='register__numTests'
                                        data={[...Array(tests.length)].map((_, i) => i + 1)}
                                        onSelect={(value) => getTestPrice(value)}
                                        value={numTests}
                                        required
                                    />
                                    {testDiscount
                                        ?   <p className='register__coursePrice--discount'>
                                                <span>${testPrice.toFixed(2)}</span>
                                                ${(testPrice - testDiscount).toFixed(2)}
                                            </p>
                                        :   <p className='register__coursePrice'>
                                                ${testPrice.toFixed(2)}
                                            </p>
                                    }
                                    {/* {cartBundles.filter(bundle => bundle.tests.length > 0).filter(bundle => bundle.discount).length > 0
                                        ?   <p className='register__coursePrice--discount'>
                                                {cartBundles.filter(bundle => bundle.tests.length > 0).filter(bundle => bundle.discount).length > 0 &&
                                                    <span>
                                                        ${(cartBundles.filter(bundle => bundle.tests.length > 0).reduce((total, bundle) => (
                                                            bundle.tests.reduce((bundleTotal, test, t) => (
                                                                bundle.numRequired > t ? bundleTotal + test.price : bundleTotal
                                                            ), 0) + total
                                                        ), 0) + cartTests.reduce((total, test) => total + test.price, 0)).toFixed(2)}
                                                    </span>
                                                }
                                                ${(cartBundles.filter(bundle => bundle.tests.length > 0).reduce((total, bundle) => (
                                                    bundle.discount?.type === 'Percent'
                                                        ?   bundle.tests.reduce((bundleTotal, test, t) => (
                                                                bundle.numRequired > t ? bundleTotal + test.price : bundleTotal
                                                            ), 0) * (100 - bundle.discount.value) / 100 + total
                                                        :   bundle.discount?.type === 'Amount'
                                                        ?   bundle.tests.reduce((bundleTotal, test, t) => (
                                                                bundle.numRequired > t ? bundleTotal + test.price : bundleTotal
                                                            ), 0) - bundle.discount.value + total
                                                        :   bundle.tests.reduce((bundleTotal, test, t) => (
                                                                bundle.numRequired > t ? bundleTotal + test.price : bundleTotal
                                                            ), 0) + total
                                                ), 0) + cartTests.reduce((total, test) => total + test.price, 0)).toFixed(2)}
                                            </p>
                                        :   <p className='register__coursePrice'>
                                                ${(cartBundles.filter(bundle => bundle.tests.length > 0).reduce((total, bundle) => (
                                                    bundle.tests.reduce((bundleTotal, test, t) => (
                                                        bundle.numRequired > t ? bundleTotal + test.price : bundleTotal
                                                    ), 0) + total
                                                ), 0) + cartTests.reduce((total, test) => total + test.price, 0)).toFixed(2)}
                                            </p>
                                    } */}
                                </div>
                                <div className='register__courseColumn'>
                                    <ButtonLink
                                        className='register__courseButton'
                                        href={`https://thesatcrashcourse.com/test-center/`}
                                        secondary
                                    >
                                        LEARN MORE
                                    </ButtonLink>
                                    {cartBundles.reduce((total, bundle) => (bundle.tests.length > 0 && bundle.numRequired
                                        ? 0
                                        : total
                                    ), 0) + cartTests.length === 0
                                        ?   <Button 
                                                className='register__courseButton'
                                                onClick={updateTestsInCart}
                                            >
                                                ADD TO CART
                                            </Button>
                                        :   numTests !== cartBundles.reduce((total, bundle) => (bundle.tests.length > 0 && bundle.numRequired
                                                ? 0
                                                : total
                                            ), 0) + cartTests.length
                                        ?   <Button 
                                                className='register__courseButton'
                                                onClick={updateTestsInCart}
                                            >
                                                UPDATE CART
                                            </Button>
                                        :   <Button 
                                                className='register__courseButton'
                                                disabled
                                            >
                                                CART ADDED
                                            </Button>
                                    }
                                </div>
                            </div>
                        </div>
                    </>}
                </div>
                <div className='register__bundles'>
                    {bundles.filter(bundle => (
                        !bundle.courses.some(course => (
                            classroomCourses.some(classroomCourse => (
                                classroomCourse.urlSlug === course.urlSlug
                            ))
                        )) && (
                            cartBundles.filter(bundle => bundle.tests.length === 0).some(cartBundle => (
                                cartBundle.courses.every(cartBundleCourse => (
                                    bundle.courses.some(course => (
                                        cartBundleCourse.urlSlug === course.urlSlug
                                    ))
                                )) && bundle.courses.length >= cartBundle.courses.length
                            )) || bundle.courses.every(course => (
                                cartBundles.filter(bundle => bundle.tests.length === 0).every(cartBundle => (
                                    cartBundle.courses.every(cartBundleCourse => (
                                        course.urlSlug !== cartBundleCourse.urlSlug
                                    ))
                                ))
                            ))
                        ) && (
                            !powerSet(cartCourses).some(combination => (
                                bundles.filter(anotherBundle => anotherBundle !== bundle).some(anotherBundle => (
                                    bundle.courses.every(course => (
                                        anotherBundle.courses.some(bundleCourse => (
                                            course.urlSlug === bundleCourse.urlSlug
                                        ))
                                    )) && anotherBundle.courses.every(bundleCourse => (
                                        [...bundle.courses, ...combination].some(course => (
                                            course.urlSlug === bundleCourse.urlSlug
                                        ))
                                    ))
                                ))
                            ))
                        )
                    )).map(bundle => (
                        <div className='register__bundle' key={bundle.name}>
                            <div className='register__bundleInfo'>
                                <h2 className='register__bundleHeader'>
                                    {bundle.name}
                                </h2>
                                <table className='register__bundleTable'>
                                    <tbody>
                                        {bundle.courses.map(course => (
                                            <tr key={course.name}>
                                                <th>{course.name}</th>
                                                {course.period
                                                    ? <td>{moment.duration(course.period).asWeeks()}-Week Course</td>
                                                    : <td>${course.tuition.toFixed(2)}</td>
                                                }
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className='register__bundleTableDivider'></td>
                                            <td className='register__bundleTableDivider'></td>
                                        </tr>
                                        <tr>
                                            <th>Total course fee</th>
                                            <td>
                                                {bundle.discount?.type === 'Percent'
                                                ?   <p className='register__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, { tuition }) => total + tuition, 0)*(100 - bundle.discount.value)/100).toFixed(2)}
                                                    </p>
                                                :   bundle.discount?.type === 'Amount'
                                                ?   <p className='register__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, course) => total + course.tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, course) => total + course.tuition, 0) - bundle.discount.value).toFixed(2)}
                                                    </p>
                                                :   <p>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</p>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {bundle.courses.every(course => (
                                    cartBundles.some(cartBundle => (
                                        cartBundle.courses.some(cartBundleCourse => (
                                            cartBundleCourse.urlSlug === course.urlSlug
                                        ))
                                    ))
                                ))
                                ?   <Button
                                        className='register__bundleButton'
                                        disabled
                                    >
                                        CART ADDED
                                    </Button>
                                :   <Button
                                        className='register__bundleButton'
                                        onClick={() => addBundleToCart(bundle.id)}
                                    >
                                        ADD TO CART
                                    </Button>
                            }
                        </div>
                    ))}
                    {/* {bundles.map(bundle => (
                            bundle.courses.some(course => (
                                classroomCourses.some(classroomCourse => (
                                    classroomCourse.urlSlug === course.urlSlug
                                ))
                            )) || bundle.courses.every(course => (
                                cartCourses.some(cartCourse => (
                                    cartCourse.urlSlug !== course.urlSlug
                                )) 
                            )) 
                        ) ? null : (
                        <div className='register__bundle' key={bundle.name}>
                            <div className='register__bundleInfo'>
                                <h2 className='register__bundleHeader'>
                                    {bundle.name}
                                </h2>
                                <table className='register__bundleTable'>
                                    <tbody>
                                        {bundle.courses.map(course => (
                                            <tr key={course.name}>
                                                <th>{course.name}</th>
                                                {course.period
                                                    ? <td>{moment.duration(course.period).asWeeks()}-Week Course</td>
                                                    : <td>${course.tuition.toFixed(2)}</td>
                                                }
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className='register__bundleTableDivider'></td>
                                            <td className='register__bundleTableDivider'></td>
                                        </tr>
                                        <tr>
                                            <th>Total course fee</th>
                                            <td>
                                                {bundle.discount?.type === 'Percent'
                                                ?   <p className='register__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, { tuition }) => total + tuition, 0)*(100 - bundle.discount.value)).toFixed(2)}
                                                    </p>
                                                :   bundle.discount?.type === 'Amount'
                                                ?   <p className='register__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, course) => total + course.tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, course) => total + course.tuition, 0) - bundle.discount.value).toFixed(2)}
                                                    </p>
                                                :   <p>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</p>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {bundle.courses.every(course => (
                                    cartBundles.some(cartBundle => (
                                        cartBundle.courses.some(cartBundleCourse => (
                                            cartBundleCourse.urlSlug === course.urlSlug
                                        ))
                                    ))
                                ))
                                ?   <Button
                                        className='register__bundleButton'
                                        disabled
                                    >
                                        CART ADDED
                                    </Button>
                                :   <Button
                                        className='register__bundleButton'
                                        onClick={() => addBundleToCart(bundle.id)}
                                    >
                                        ADD TO CART
                                    </Button>
                            }
                        </div>
                    ))} */}
                </div>
            </div>
        </div>
    );
}

export default Courses;